import { useEffect, useMemo, useRef } from 'react'
import { Euler, InstancedMesh, Matrix4, Quaternion, Vector3 } from 'three'

import type { SplineInstanceData } from '@hooks'
import {
  BOX_GEOMETRY,
  METAL_MATERIAL,
  METAL_MATERIAL_TRANSPARENT,
  WOOD_MATERIAL,
  WOOD_MATERIAL_LIGHT_TRANSPARENT,
} from '@utils'

// Mirror of SplineModel local transforms
const TIE_SCALE_NORMAL = new Vector3(0.5, 0.05, 0.12)
const TIE_SCALE_SWITCH = new Vector3(0.4, 0.05, 0.12)
const TIE_OFFSET_Y = -0.01
const BOLT_SCALE = new Vector3(0.04, 0.03, 0.05)
const BOLT_OFFSET_L = new Vector3(0.17, 0.02, 0)
const BOLT_OFFSET_R = new Vector3(-0.17, 0.02, 0)

interface Props {
  instances: SplineInstanceData[]
}

// Build a world matrix for one mesh inside a SplineModel.
// parentPos/parentRotY = spline pose; localOffset/localScale = mesh local transform.
function buildMatrix(
  parentPos: Vector3,
  parentRotY: number,
  localOffset: Vector3,
  localScale: Vector3,
  out = new Matrix4(),
) {
  const q = new Quaternion().setFromEuler(new Euler(0, parentRotY, 0))
  // Rotate local offset by parent rotation
  const worldOffset = localOffset.clone().applyQuaternion(q)
  const worldPos = parentPos.clone().add(worldOffset)
  out.compose(worldPos, q, localScale)
  return out
}

export function SplinesInstancedManager({ instances }: Props) {
  const { normal, switches } = useMemo(
    () => ({
      normal: instances.filter(i => !i.isSwitch),
      switches: instances.filter(i => i.isSwitch),
    }),
    [instances],
  )

  const tieNormalRef = useRef<InstancedMesh>(null)
  const tieSwitchRef = useRef<InstancedMesh>(null)
  const boltNormalRef = useRef<InstancedMesh>(null) // left + right = count * 2
  const boltSwitchRef = useRef<InstancedMesh>(null)

  // Set matrices whenever instances change
  useEffect(() => {
    const mat = new Matrix4()

    // --- normal ties ---
    const tieNormal = tieNormalRef.current
    if (tieNormal) {
      normal.forEach((inst, i) => {
        buildMatrix(
          inst.position,
          inst.rotationY,
          new Vector3(0, TIE_OFFSET_Y, 0),
          TIE_SCALE_NORMAL,
          mat,
        )
        tieNormal.setMatrixAt(i, mat)
      })
      tieNormal.instanceMatrix.needsUpdate = true
    }

    // --- switch ties ---
    const tieSwitch = tieSwitchRef.current
    if (tieSwitch) {
      switches.forEach((inst, i) => {
        buildMatrix(
          inst.position,
          inst.rotationY,
          new Vector3(0, TIE_OFFSET_Y, 0),
          TIE_SCALE_SWITCH,
          mat,
        )
        tieSwitch.setMatrixAt(i, mat)
      })
      tieSwitch.instanceMatrix.needsUpdate = true
    }

    // --- normal bolts (left + right interleaved) ---
    const boltNormal = boltNormalRef.current
    if (boltNormal) {
      normal.forEach((inst, i) => {
        buildMatrix(inst.position, inst.rotationY, BOLT_OFFSET_L, BOLT_SCALE, mat)
        boltNormal.setMatrixAt(i * 2, mat)
        buildMatrix(inst.position, inst.rotationY, BOLT_OFFSET_R, BOLT_SCALE, mat)
        boltNormal.setMatrixAt(i * 2 + 1, mat)
      })
      boltNormal.instanceMatrix.needsUpdate = true
    }

    // --- switch bolts ---
    const boltSwitch = boltSwitchRef.current
    if (boltSwitch) {
      switches.forEach((inst, i) => {
        buildMatrix(inst.position, inst.rotationY, BOLT_OFFSET_L, BOLT_SCALE, mat)
        boltSwitch.setMatrixAt(i * 2, mat)
        buildMatrix(inst.position, inst.rotationY, BOLT_OFFSET_R, BOLT_SCALE, mat)
        boltSwitch.setMatrixAt(i * 2 + 1, mat)
      })
      boltSwitch.instanceMatrix.needsUpdate = true
    }
  }, [instances, normal, switches])

  if (instances.length === 0) return null

  return (
    <>
      {/* Normal track ties */}
      {normal.length > 0 && (
        <instancedMesh
          receiveShadow
          ref={tieNormalRef}
          args={[BOX_GEOMETRY, WOOD_MATERIAL, normal.length]}
          frustumCulled={false}
        />
      )}

      {/* Switch track ties */}
      {switches.length > 0 && (
        <instancedMesh
          ref={tieSwitchRef}
          args={[BOX_GEOMETRY, WOOD_MATERIAL_LIGHT_TRANSPARENT, switches.length]}
          frustumCulled={false}
          renderOrder={-1}
        />
      )}

      {/* Normal track bolts (left + right) */}
      {normal.length > 0 && (
        <instancedMesh
          receiveShadow
          ref={boltNormalRef}
          args={[BOX_GEOMETRY, METAL_MATERIAL, normal.length * 2]}
          frustumCulled={false}
        />
      )}

      {/* Switch track bolts */}
      {switches.length > 0 && (
        <instancedMesh
          ref={boltSwitchRef}
          args={[BOX_GEOMETRY, METAL_MATERIAL_TRANSPARENT, switches.length * 2]}
          frustumCulled={false}
          renderOrder={-1}
        />
      )}
    </>
  )
}
